import type { ProSearchData } from '../services/search.service';

/**
 * Swap freshly re-read rows for one PRO into a grid's existing row list.
 *
 * Used after a per-row trace: the trace wrote the order, so that PRO's rows on
 * screen are stale in exactly the fields the user ran the trace to see.
 *
 * Pairs POSITIONALLY. A pro is usually one row, but a row-expanding added column
 * (invoice line items) fans it into one row per line item on both sides, and both
 * sides come from the same backend ordering — so the Nth existing row for the pro
 * pairs with the Nth fresh one. Existing rows beyond what came back are left
 * untouched rather than dropped: a refresh that returns less than the grid holds
 * means the re-read disagrees with the search, and silently deleting a user's rows
 * is far worse than leaving one stale.
 *
 * Nothing is removed and nothing is added — only matched rows are replaced — so
 * row count, order, scroll position and selection all survive.
 */
export function mergeRefreshedProRows<T>(
  rows: T[],
  isMatch: (row: T) => boolean,
  fresh: ReadonlyArray<ProSearchData>,
  merge: (row: T, fresh: ProSearchData) => T,
): T[] {
  if (!fresh.length) return rows;
  let seen = 0;
  return rows.map((row) => {
    if (!isMatch(row)) return row;
    const next = fresh[seen++];
    return next ? merge(row, next) : row;
  });
}

/** True when a row belongs to this PRO. String-compared so a numeric pronumber and
 *  a template's "T110" both resolve, and so a row whose pronumber arrived as a
 *  string still matches a numeric argument. */
export const isProRow = (rowPro: unknown, pronumber: number | string): boolean =>
  String(rowPro ?? '') === String(pronumber);
